'use client';

import { useFrame } from '@react-three/fiber';
import type { FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Group, LineSegments, Points } from 'three';
import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Color,
  NormalBlending,
  ShaderMaterial,
} from 'three';
import { advanceFlight, geodeticToScene, type FlightState } from '../geo';
import { skyState, subscribeSky } from '../skyStore';

const TRAIL_SAMPLES = 12;
const MAX_RANGE_M = 300000;

const aircraftVert = /* glsl */ `
  uniform float uSize;
  void main() {
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mv;
    gl_PointSize = uSize;
  }
`;

const reticleVert = /* glsl */ `
  uniform float uSize;
  attribute float aHover;
  varying float vHover;
  void main() {
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mv;
    gl_PointSize = uSize;
    vHover = aHover;
  }
`;

const aircraftFrag = /* glsl */ `
  uniform vec3 uColor;
  uniform float uOpacity;
  void main() {
    float d = length(gl_PointCoord - 0.5);
    float a = smoothstep(0.5, 0.18, d) * uOpacity;
    if (a < 0.02) discard;
    gl_FragColor = vec4(uColor, a);
  }
`;

const reticleFrag = /* glsl */ `
  uniform vec3 uColor;
  uniform float uOpacity;
  varying float vHover;
  void main() {
    if (vHover < 0.5) discard;
    vec2 p = abs(gl_PointCoord * 2.0 - 1.0);
    float outer = 0.92;
    float inner = 0.48;
    float thick = 0.16;
    float armX =
      step(inner, p.x) * step(p.x, outer) *
      step(outer - thick, p.y) * step(p.y, outer);
    float armY =
      step(inner, p.y) * step(p.y, outer) *
      step(outer - thick, p.x) * step(p.x, outer);
    float a = clamp(armX + armY, 0.0, 1.0);
    if (a < 0.02) discard;
    gl_FragColor = vec4(uColor, a * uOpacity);
  }
`;

const trailVert = /* glsl */ `
  attribute float aAlpha;
  varying float vAlpha;
  varying float vDist;
  void main() {
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vDist = -mv.z;
    gl_Position = projectionMatrix * mv;
    vAlpha = aAlpha;
  }
`;

const trailFrag = /* glsl */ `
  uniform vec3 uColor;
  uniform float uOpacity;
  varying float vAlpha;
  varying float vDist;
  void main() {
    float haze = 1.0 - smoothstep(20000.0, 220000.0, vDist);
    float a = vAlpha * uOpacity * mix(0.25, 1.0, haze);
    if (a < 0.004) discard;
    gl_FragColor = vec4(uColor, a);
  }
`;

const TRAFFIC_LAYER = 1;

export const FlightTraffic: FC = () => {
  const [flights, setFlights] = useState<FlightState[]>(skyState.flights);
  useEffect(() => subscribeSky(() => setFlights(skyState.flights)), []);

  const groupRef = useRef<Group>(null);
  const aircraftRef = useRef<Points>(null);
  const reticleRef = useRef<Points>(null);
  const trailRef = useRef<LineSegments>(null);

  const { pointGeometry, trailGeometry } = useMemo(() => {
    const count = Math.max(1, flights.length);
    const pointGeometry = new BufferGeometry();
    pointGeometry.setAttribute(
      'position',
      new BufferAttribute(new Float32Array(count * 3), 3)
    );
    pointGeometry.setAttribute(
      'aHover',
      new BufferAttribute(new Float32Array(count), 1)
    );

    const segments = count * TRAIL_SAMPLES;
    const trailGeometry = new BufferGeometry();
    trailGeometry.setAttribute(
      'position',
      new BufferAttribute(new Float32Array(segments * 2 * 3), 3)
    );
    trailGeometry.setAttribute(
      'aAlpha',
      new BufferAttribute(new Float32Array(segments * 2), 1)
    );
    return { pointGeometry, trailGeometry };
  }, [flights.length]);

  useEffect(
    () => () => {
      pointGeometry.dispose();
      trailGeometry.dispose();
    },
    [pointGeometry, trailGeometry]
  );

  const aircraftMaterial = useMemo(
    () =>
      new ShaderMaterial({
        vertexShader: aircraftVert,
        fragmentShader: aircraftFrag,
        uniforms: {
          uSize: { value: 3.5 },
          uColor: { value: new Color(1, 1, 1) },
          uOpacity: { value: 0 },
        },
        transparent: true,
        // writes depth: the atmosphere hazes it and clouds can occlude it
        depthWrite: true,
        depthTest: true,
        blending: NormalBlending,
      }),
    []
  );

  const reticleMaterial = useMemo(
    () =>
      new ShaderMaterial({
        vertexShader: reticleVert,
        fragmentShader: reticleFrag,
        uniforms: {
          uSize: { value: 22 },
          uColor: { value: new Color(1, 1, 1) },
          uOpacity: { value: 0.5 },
        },
        transparent: true,
        depthWrite: false,
        depthTest: false,
        blending: NormalBlending,
      }),
    []
  );

  const trailMaterial = useMemo(
    () =>
      new ShaderMaterial({
        vertexShader: trailVert,
        fragmentShader: trailFrag,
        uniforms: {
          uColor: { value: new Color(1, 1, 1) },
          uOpacity: { value: 0.5 },
        },
        transparent: true,
        depthWrite: false,
        depthTest: true,
        blending: AdditiveBlending,
      }),
    []
  );

  useEffect(
    () => () => {
      aircraftMaterial.dispose();
      reticleMaterial.dispose();
      trailMaterial.dispose();
    },
    [aircraftMaterial, reticleMaterial, trailMaterial]
  );

  useEffect(() => {
    groupRef.current?.traverse((object) => object.layers.set(TRAFFIC_LAYER));
  }, []);

  useFrame(() => {
    const points = aircraftRef.current;
    const reticles = reticleRef.current;
    const trails = trailRef.current;
    if (!points || !trails || !reticles) return;

    const pos = pointGeometry.getAttribute('position') as BufferAttribute;
    const hover = pointGeometry.getAttribute('aHover') as BufferAttribute;
    const cfg = skyState.traffic;
    const hoveredId = skyState.hoveredFlight;
    const tPos = trailGeometry.getAttribute('position') as BufferAttribute;
    const tAlpha = trailGeometry.getAttribute('aAlpha') as BufferAttribute;
    const nowSec = Date.now() / 1000;

    const reveal = skyState.trafficReveal;
    aircraftMaterial.uniforms.uOpacity.value = reveal;
    aircraftMaterial.uniforms.uSize.value = cfg.dotSize;
    reticleMaterial.uniforms.uOpacity.value = reveal * 0.7;
    reticleMaterial.uniforms.uSize.value = cfg.reticleSize;
    trailMaterial.uniforms.uOpacity.value = cfg.trailOpacity;
    points.visible = reveal > 0.01;
    reticles.visible = reveal > 0.01;

    let seg = 0;
    for (let i = 0; i < flights.length; i++) {
      const f = flights[i];
      // Extrapolate from the fix's own timestamp: OpenSky serves positions
      // ~18s old, so measuring from fetch time makes them snap on refresh.
      const age = Math.min(240, Math.max(0, nowSec - f.t));
      const here = advanceFlight(f, age);
      const p = geodeticToScene(here.lat, here.lon, f.alt);
      const range = Math.hypot(p.x, p.z);
      const visible = range < MAX_RANGE_M;

      // Park hidden traffic below the ground rather than branching the draw.
      pos.setXYZ(i, p.x, visible ? p.y : -1e6, p.z);
      hover.setX(i, !cfg.reticleOnHover || hoveredId === f.id ? 1 : 0);

      let previous = p;
      for (let s = 0; s < TRAIL_SAMPLES; s++) {
        const head = 1 - s / TRAIL_SAMPLES;
        const tail = 1 - (s + 1) / TRAIL_SAMPLES;
        const v0 = seg * 2;
        const v1 = v0 + 1;
        if (visible) {
          const time = age - ((s + 1) / TRAIL_SAMPLES) * cfg.trailSeconds;
          const next = advanceFlight(f, time);
          const position = geodeticToScene(next.lat, next.lon, f.alt);
          tPos.setXYZ(v0, previous.x, previous.y, previous.z);
          tPos.setXYZ(v1, position.x, position.y, position.z);
          previous = position;
        } else {
          tPos.setXYZ(v0, 0, -1e6, 0);
          tPos.setXYZ(v1, 0, -1e6, 0);
        }
        tAlpha.setX(v0, head * head);
        tAlpha.setX(v1, tail * tail);
        seg++;
      }
    }

    pos.needsUpdate = true;
    hover.needsUpdate = true;
    tPos.needsUpdate = true;
    tAlpha.needsUpdate = true;
    pointGeometry.computeBoundingSphere();
    trailGeometry.computeBoundingSphere();
  });

  if (flights.length === 0) return null;

  return (
    <group ref={groupRef}>
      <lineSegments
        ref={trailRef}
        geometry={trailGeometry}
        material={trailMaterial}
        frustumCulled={false}
        renderOrder={1}
      />
      <points
        ref={aircraftRef}
        geometry={pointGeometry}
        material={aircraftMaterial}
        frustumCulled={false}
      />
      <points
        ref={reticleRef}
        geometry={pointGeometry}
        material={reticleMaterial}
        frustumCulled={false}
        renderOrder={20}
      />
    </group>
  );
};
